import { apiUrl } from "@/config/api";
import { env } from "@/config/env";
import { session, useSessionStore } from "@/lib/auth";
import { expiresWithin } from "@/lib/auth/token";
import { ApiError, type ProblemDetails } from "./errors";
import { client } from "./generated/client.gen";

/**
 * Configures the generated hey-api fetch client once for the whole app: base
 * URL, bearer auth from the session store, silent token refresh, and ApiError
 * normalisation. Import this module for its side effect from app/providers
 * (never from features).
 *
 * Refresh happens two ways. The request interceptor renews the access token
 * just before it expires, which is what actually keeps a long editing session
 * alive. The response interceptor is the fallback for a token that was revoked
 * or rejected unexpectedly.
 */
const REFRESH_PATH = "/auth/refresh" as const;
const AUTH_FREE_PATHS = ["/auth/login", REFRESH_PATH];

/** Renew this many seconds before expiry, to cover request latency and clock skew. */
const RENEW_BEFORE_SECONDS = 60;

client.setConfig({
  baseUrl: env.VITE_API_ORIGIN,
  credentials: "include",
  auth: () => session.get().accessToken ?? undefined,
});

let refreshing: Promise<string | null> | null = null;

/**
 * Refresh the access token using the httpOnly cookie. Concurrent callers share
 * one in-flight request: the server rotates the refresh token, so a second
 * parallel call would present an already-revoked one and fail.
 */
async function refreshAccessToken(): Promise<string | null> {
  if (!refreshing) {
    refreshing = fetch(apiUrl(REFRESH_PATH), { method: "POST", credentials: "include" })
      .then(async (res) => {
        if (!res.ok) return null;
        const body = (await res.json()) as { accessToken?: string };
        return body.accessToken ?? null;
      })
      .catch(() => null)
      .then((token) => {
        const current = useSessionStore.getState();
        if (token) {
          if (current.user) current.setSession({ accessToken: token, user: current.user });
        } else if (current.user) {
          // The cookie is gone or was revoked: the stored profile is stale.
          current.clear();
        }
        return token;
      })
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

function isAuthFree(url: string): boolean {
  const { pathname } = new URL(url);
  return AUTH_FREE_PATHS.some((path) => pathname.endsWith(path));
}

client.interceptors.request.use(async (request) => {
  if (isAuthFree(request.url)) return request;

  const { accessToken: token, user } = session.get();
  // No token but a signed-in profile is the state right after a reload: the
  // token is never stored, so one is fetched before the first request goes.
  if (!token && !user) return request;
  if (token && !expiresWithin(token, RENEW_BEFORE_SECONDS)) return request;

  const fresh = await refreshAccessToken();
  // Headers on an unsent Request are mutable, so set in place: rebuilding the
  // Request would have to re-attach the body.
  if (fresh) request.headers.set("Authorization", `Bearer ${fresh}`);
  return request;
});

client.interceptors.response.use(async (response, request) => {
  if (response.ok) return response;

  if (response.status === 401 && !isAuthFree(request.url)) {
    const token = await refreshAccessToken();
    if (token) {
      const retried = replayWith(request, token);
      if (retried) {
        const retry = await fetch(retried);
        if (retry.ok) return retry;
        response = retry;
      }
    } else {
      useSessionStore.getState().clear();
    }
  }

  let problem: ProblemDetails | undefined;
  try {
    problem = (await response.clone().json()) as ProblemDetails;
  } catch {
    problem = undefined;
  }
  throw new ApiError(response.status, problem, response.statusText);
});

/**
 * Rebuild a sent request with a new bearer token, or null when its body has
 * already been consumed and cannot be replayed.
 *
 * The header must be replaced with `set`, not merged: `Headers` treats names
 * case-insensitively and appending a second Authorization value produces
 * "Bearer old, Bearer new", which every server rejects.
 */
function replayWith(request: Request, token: string): Request | null {
  const headers = new Headers(request.headers);
  headers.set("Authorization", `Bearer ${token}`);
  try {
    return new Request(request.clone(), { headers });
  } catch {
    return null;
  }
}

export { client };
